import axios, { AxiosInstance, AxiosRequestConfig } from "axios";
import { clearAccessToken, getAccessToken, setAccessToken } from "models/auth/accessTokenStore";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

type RefreshResponse = {
    token?: string;
    accessToken?: string;
    userId?: string;
};

type ApiResponse<T> = {
    success: boolean;
    data: T | null;
    errors?: Array<{ code: string; message: string }>;
    meta?: any;
};

let refreshPromise: Promise<string | null> | null = null;

const isAuthEndpoint = (url: string | undefined): boolean => {
    if (!url) return false;
    return /\/authentication\/(login|refresh-token)/i.test(url) || /\/Authentication\/(login|refresh-token)/.test(url);
};

const getBaseForRefresh = (instanceBaseUrl?: string): string | undefined => {
    return instanceBaseUrl ?? API_BASE_URL;
};

const requestRefreshToken = async (baseURL?: string): Promise<string | null> => {
    const trimmedBase = (baseURL || '').replace(/\/+$/, '');
    const url = trimmedBase ? `${trimmedBase}/authentication/refresh-token` : `/authentication/refresh-token`;

    try {
        const response = await axios.post<ApiResponse<RefreshResponse> | RefreshResponse>(
            url,
            {},
            {
                withCredentials: true,
                headers: { "Content-Type": "application/json" },
            }
        );

        const body: any = response.data;
        const nextToken: string | undefined = body?.data?.token ?? body?.data?.accessToken ?? body?.token ?? body?.accessToken;
        if (!nextToken) return null;

        setAccessToken(nextToken);
        return nextToken;
    } catch {
        return null;
    }
};

export const createAxiosWithInterceptors = (baseURL?: string): AxiosInstance => {
    const instance = axios.create({
        baseURL,
        headers: {
            "Content-Type": "application/json"
        },
    });

    instance.interceptors.request.use((config) => {
        const token = getAccessToken();

        if (token) {
            (config.headers as any).Authorization = `Bearer ${token}`;
        }

        return config;
    }, (error) => {
        return Promise.reject(error);
    });

    instance.interceptors.response.use(
        (response) => response,
        async (error) => {
            const status = error?.response?.status;
            const originalRequest = error?.config as (AxiosRequestConfig & { _retry?: boolean }) | undefined;

            if (status !== 401 || !originalRequest) {
                return Promise.reject(error);
            }

            const requestUrl = String(originalRequest.url || '');
            // A rejected login must stay on the login page so the caller can
            // surface its visible error. Protected requests that have already
            // failed a refresh still redirect to restart authentication.
            if (isAuthEndpoint(requestUrl)) {
                clearAccessToken();
                return Promise.reject(error);
            }

            if (originalRequest._retry) {
                clearAccessToken();
                window.location.href = "/login";
                return Promise.reject(error);
            }

            originalRequest._retry = true;

            if (!refreshPromise) {
                refreshPromise = requestRefreshToken(getBaseForRefresh(instance.defaults.baseURL));
                refreshPromise.finally(() => {
                    refreshPromise = null;
                });
            }

            const nextToken = await refreshPromise;
            if (!nextToken) {
                clearAccessToken();
                window.location.href = "/login";
                return Promise.reject(error);
            }

            originalRequest.headers = originalRequest.headers ?? {};
            (originalRequest.headers as any).Authorization = `Bearer ${nextToken}`;
            return instance(originalRequest);
        }
    );

    return instance;
};

// Default axios client used across the app (keeps existing behavior)
const apiClient = createAxiosWithInterceptors(API_BASE_URL);

// Axios instance WITHOUT a baseURL for generated OpenAPI clients
export const apiClientNoBase = createAxiosWithInterceptors(undefined);

// 🔹 Method to REMOVE "Content-Type: application/json"
export const removeJsonHeader = () => {
    delete (apiClient.defaults.headers as any)["Content-Type"];
};

// 🔹 Method to RE-ADD "Content-Type: application/json"
export const addJsonHeader = () => {
    (apiClient.defaults.headers as any)["Content-Type"] = "application/json";
};

export default apiClient;
